import React, { useState } from 'react';
import { CreditCard, ExternalLink, Loader, AlertCircle, Check, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { useTheme } from '../hooks/useTheme';
import { SubscriptionService } from '../lib/subscription';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface BillingManagementProps {}

export function BillingManagement({}: BillingManagementProps = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { subscription, hasValidSubscription, subscriptionPlan } = useSubscription();
  const { isDarkMode } = useTheme();

  const handleManageBilling = async () => {
    if (!user) {
      setError('You must be logged in to manage billing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await SubscriptionService.createPortalSession();
      
      if (!result?.url) {
        throw new Error('Failed to create billing session');
      }

      window.open(result.url, '_blank');

    } catch (err) {
      console.error('Error opening billing portal:', err);
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionStatus = () => {
    if (!subscription) return 'No active subscription';
    
    switch (subscription.status) {
      case 'active':
        return 'Active';
      case 'canceled':
        return 'Canceled';
      case 'past_due':
        return 'Past Due';
      case 'unpaid':
        return 'Unpaid';
      default:
        return subscription.status || 'Unknown';
    }
  };

  const getStatusColor = () => {
    if (!subscription) return isDarkMode ? 'text-gray-400' : 'text-gray-600';
    
    switch (subscription.status) {
      case 'active':
        return 'text-green-600';
      case 'canceled':
        return 'text-red-600';
      case 'past_due':
      case 'unpaid':
        return 'text-yellow-600';
      default:
        return isDarkMode ? 'text-gray-400' : 'text-gray-600';
    }
  };

  const getStatusIcon = () => {
    if (!subscription) return <X className="w-5 h-5" />;
    
    switch (subscription.status) {
      case 'active':
        return <Check className="w-5 h-5 text-green-600" />;
      case 'canceled':
      case 'past_due':
      case 'unpaid':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      default:
        return <X className="w-5 h-5" />;
    }
  };

  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'family_plan':
        return 'Family Plan';
      case 'parent_plan':
        return 'Parent Plan';
      case 'school_starter':
        return 'School Starter';
      default:
        return plan.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Billing & Subscription
        </h1>
        <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Manage your subscription, billing details, and payment methods
        </p>
      </div>

      {/* Subscription Status Card */}
      <div className={`rounded-2xl p-6 border shadow-sm transition-colors duration-200 ${
        isDarkMode 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Current Subscription
          </h2>
          <div className="flex items-center space-x-2">
            {getStatusIcon()}
            <span className={`font-medium ${getStatusColor()}`}>
              {getSubscriptionStatus()}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Plan
            </div>
            <div className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {subscriptionPlan ? getPlanDisplayName(subscriptionPlan) : 'No active plan'}
            </div>
          </div>
          
          {subscription?.currentPeriodEnd && (
            <div>
              <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {subscription.status === 'canceled' ? 'Access until' : 'Next billing date'}
              </div>
              <div className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>

        {subscription?.status === 'past_due' && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Payment Required</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Your subscription payment is past due. Please update your payment method to continue using Neurocademy.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Billing Management Actions */}
      <div className={`rounded-2xl p-6 border shadow-sm transition-colors duration-200 ${
        isDarkMode 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Manage Your Subscription
        </h2>
        
        {subscription?.status === 'lifetime' ? (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Check className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">Lifetime Access</h3>
                  <p className="text-sm text-green-700 mt-1">
                    You have lifetime access to Neurocademy! No recurring billing or subscription management needed.
                    Your account will remain active indefinitely.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Use the Stripe Customer Portal to:
            </p>
            
            <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <li className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span>Update payment methods and billing information</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span>View billing history and download invoices</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span>Upgrade or downgrade your subscription plan</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                <span>Cancel your subscription if needed</span>
              </li>
            </ul>

            <div className="pt-4">
              <button
                onClick={handleManageBilling}
                disabled={loading}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4" />
                )}
                <span>{loading ? 'Loading...' : 'Manage Billing'}</span>
                {!loading && <ExternalLink className="w-4 h-4" />}
              </button>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Support Information */}
      <div className={`rounded-2xl p-6 border shadow-sm transition-colors duration-200 ${
        isDarkMode 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Need Help?
        </h2>
        
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          If you need assistance with your subscription or billing, please don't hesitate to contact our support team.
        </p>

        <div className="space-y-2">
          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="font-medium">Email:</span> support@neurocademy.net
          </div>
          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="font-medium">Response time:</span> Within 24 hours
          </div>
        </div>
      </div>
    </div>
  );
}